import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  Dictionary,
  FileService,
} from '../../../../shared/services/file.service';
import { File as phxFile } from '../../../../shared/models/file';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from '../../../../components/messageBox/messageBox.component';
import { IParentEntity } from 'app/shared/interfaces/entity';
import { catchError, map, take } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { DefaultDataService } from 'app/shared/services/default-data.service';

@Component({
  selector: 'upload-dialog',
  templateUrl: './upload-dialog.component.html',
  styleUrls: ['./upload-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UploadDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('file') file;

  public files: Set<File> = new Set();
  public fileCount = 0;

  progress;

  filesDnD: Array<any> = [];
  canAddFiles = true;
  canBeClosed = true;
  canDeleteFiles = true;
  primaryButtonText = 'UPLOAD';
  showCancelButton = true;
  uploading = false;
  uploadSuccessful = false;
  url = 'api/files';
  acceptFiles = '';

  existingFiles: phxFile[];
  dragAreaClass: string;

  fileSummaryList: Dictionary<string> = {};

  constructor(
    public dialogRef: MatDialogRef<UploadDialogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: IParentEntity,
    public fileService: FileService,
    public defaultDataService: DefaultDataService
  ) {
    if (this.data.type.toLowerCase() === 'tasktype') {
      this.acceptFiles = '.docx';
    } else {
      this.acceptFiles = this.defaultDataService.allowedFileExtensions
        .split(';')
        .join(',')
        .split('.')
        .join('.');
    }

    // get the existing files, if any
    this.fileService
      .getExistingFiles(true, true)
      .pipe(take(1))
      .subscribe((files: phxFile[]) => {
        this.existingFiles = files.filter((item) => !item.isDirectory);
      });
  }

  ngOnInit() {
    //this.dragAreaClass = 'dragarea';
  }

  // @HostListener('dragover', ['$event']) onDragOver(event: any) {
  //   this.dragAreaClass = 'droparea';
  //   event.preventDefault();
  // }
  // @HostListener('dragenter', ['$event']) onDragEnter(event: any) {
  //   this.dragAreaClass = 'droparea';
  //   event.preventDefault();
  // }
  // @HostListener('dragend', ['$event']) onDragEnd(event: any) {
  //   this.dragAreaClass = 'dragarea';
  //   event.preventDefault();
  // }
  // @HostListener('dragleave', ['$event']) onDragLeave(event: any) {
  //   this.dragAreaClass = 'dragarea';
  //   event.preventDefault();
  // }
  // @HostListener('drop', ['$event']) async onDrop(event: any) {

  ngAfterViewInit() {
    let dropzone = document.getElementById('dropzone');

    dropzone.addEventListener('dragover', (event: any) => {
      event.stopPropagation();
      event.preventDefault();
      // Style the drag-and-drop as a "copy file" operation.
      dropzone?.classList.add('drag-over');
      event.dataTransfer.dropEffect = 'copy';
    });

    dropzone.addEventListener('dragleave', (event: any) => {
      dropzone?.classList.remove('drag-over');
    });

    dropzone.addEventListener('dragend', (event: any) => {
      dropzone?.classList.remove('drag-over');
    });

    dropzone.addEventListener('drop', async (event: any) => {
      event.preventDefault();
      event.stopPropagation();

      const fileHandler = [...event.dataTransfer.items]
        .filter((file) => file.kind === 'file')
        .map((file) => file.getAsFileSystemHandle());

      let dragAndDropFileStore: Array<any> = [];

      for await (const handle of fileHandler) {
        if (handle.kind === 'directory') {
          console.log(`Directory: ${handle.name}`);
          let path = [handle.name];
          let contents: Array<any> = [];

          await this.getFilesRecursively(handle, contents, path);

          contents.forEach((file: any) => {
            file.path_components = file?.path_components?.join('/');
          });

          dragAndDropFileStore.push(...contents);
        } else {
          console.log(`File: ${handle.name}`);
          let file: any = await handle.getFile();
          file.path_components = file.name;
          dragAndDropFileStore.push(file);
        }
      }
      this.filesDnD = [...dragAndDropFileStore];
      this.fileChecker(this.filesDnD);
    });
  }

  async getFilesRecursively(
    entry: any,
    files: Array<File>,
    path: Array<string>
  ) {
    if (entry.kind === 'file') {
      const file: any = await entry.getFile();
      file.path_components = path;
      files.push(file);
    } else if (entry.kind === 'directory') {
      for await (const handle of entry.values()) {
        path.push(handle.name);
        let newPath = path.map((p) => p);
        await this.getFilesRecursively(handle, files, newPath);
        path.pop();
      }
    }
  }

  fileChecker(filesArray) {
    let bAlreadyShownInvalidFileTypeMessage = false;
    let bAlreadyShownNoFileExtensionTypeMessage = false;
    const allowedFileExtensions = this.defaultDataService.allowedFileExtensions;

    for (const file of filesArray) {
      if (file.size > this.defaultDataService.maxFileUploadSizeMb * 1024 * 1024) {
        const info = new MessageBoxParameters(
          `The specified file '${file.name}' is too large to be uploaded. The maximum file size is ${this.defaultDataService.maxFileUploadSizeMb}MB`,
          'File too large',
          MessageBoxType.Ok
        );
        this.dialog.open(MessageBoxComponent, { data: info });
      } else {
        if (this.files.size > this.defaultDataService.maxFileUploadLimit) {
          const info = new MessageBoxParameters(
            `You can only upload a maximum of ${this.defaultDataService.maxFileUploadLimit} files at a time.`,
            'Too many files',
            MessageBoxType.Ok
          );
          this.dialog.open(MessageBoxComponent, { data: info });
          break;
        }
        if (file.name.indexOf('.') === -1) {
          if (bAlreadyShownNoFileExtensionTypeMessage === false) {
            bAlreadyShownNoFileExtensionTypeMessage = true;
            const info = new MessageBoxParameters(
              `You cannot upload a file which has no file extension.`,
              'No file extension',
              MessageBoxType.Ok
            );
            this.dialog.open(MessageBoxComponent, { data: info });
          }
          continue;
        }
        const ext = file.name.substring(file.name.lastIndexOf('.'));
        if (!allowedFileExtensions.includes(ext.toLowerCase())) {
          if (bAlreadyShownInvalidFileTypeMessage === false) {
            bAlreadyShownInvalidFileTypeMessage = true;
            const info = new MessageBoxParameters(
              `The file type ${ext.replace(';', '')} cannot be uploaded.`,
              'Invalid file selected',
              MessageBoxType.Ok
            );
            this.dialog.open(MessageBoxComponent, { data: info });
          }
          continue;
        }
        if (this.data.type.toLowerCase() === 'tasktype') {
          // check File Type is valid
          if (!file.name.toLowerCase().endsWith('.docx')) {
            if (bAlreadyShownInvalidFileTypeMessage === false) {
              bAlreadyShownInvalidFileTypeMessage = true;
              const info = new MessageBoxParameters(
                `You can only upload files with a file type of .docx for task types.`,
                'Invalid file selected',
                MessageBoxType.Ok
              );
              this.dialog.open(MessageBoxComponent, { data: info });
            }
            continue;
          }
        }
        const matchedFile = this.existingFiles.find(
          (x) => x.fileName.toLowerCase() === file.name.toLowerCase()
        );
        if (matchedFile) {
          const info = new MessageBoxParameters(
            `File ${file.name} already exists.  Replace?`,
            'Duplicate File',
            MessageBoxType.YesNo
          );
          this.dialog
            .open(MessageBoxComponent, { data: info })
            .afterClosed()
            .subscribe((result) => {
              if (result === 'yes') {
                this.addFile(file);
              }
              // make sure the value is reset so if we re-select the same file again it will still fire the change event...
              // this.file.nativeElement.value = '';
            });
        } else {
          this.addFile(file);
          // make sure the value is reset so if we re-select the same file again it will still fire the change event...
          // this.file.nativeElement.value = '';
        }
      }
    }
  }

  onFilesAdded() {
    const files: { [key: string]: File } = this.file.nativeElement.files;
    this.filesDnD = Object.values(files);
    this.fileChecker(this.filesDnD);

    //26/10/2023 (YA): Commented out onfilesadded function and moved it into new function fileChecker to make it more reuseable with drag and drop. Didnt add the Nan part of the if statement as i couldnt make sense of it.

    // for (const key in files) {
    //   //console.log(key, files[key]);
    //   if (!isNaN(parseInt(key, 10))) {
    //     this.fileSummaryList[files[key].name] = '';

    //     if (
    //       files[key].size >
    //       this.fileService.maxFileUploadSizeMb * 1024 * 1024
    //     ) {
    //       const info = new MessageBoxParameters(
    //         `The specified file '${files[key].name}' is too large to be uploaded. The maximum file size is ${this.fileService.maxFileUploadSizeMb}MB`,
    //         'File too large',
    //         MessageBoxType.Ok
    //       );
    //       this.dialog.open(MessageBoxComponent, { data: info });
    //     } else {
    //       if (this.files.size > this.fileService.maxFileUploadLimit) {
    //         const info = new MessageBoxParameters(
    //           `You can only upload a maximum of ${this.fileService.maxFileUploadLimit} files at a time.`,
    //           'Too many files',
    //           MessageBoxType.Ok
    //         );
    //         this.dialog.open(MessageBoxComponent, { data: info });
    //         break;
    //       }

    //       if (files[key].name.indexOf('.') === -1) {
    //         if (bAlreadyShownNoFileExtensionTypeMessage === false) {
    //           bAlreadyShownNoFileExtensionTypeMessage = true;
    //           const info = new MessageBoxParameters(
    //             `You cannot upload a file which has no file extension.`,
    //             'No file extension',
    //             MessageBoxType.Ok
    //           );
    //           this.dialog.open(MessageBoxComponent, { data: info });
    //         }
    //         continue;
    //       }

    //       const ext = files[key].name.substring(
    //         files[key].name.lastIndexOf('.')
    //       );
    //       if (!allowedFileExtensions.includes(ext.toLowerCase())) {
    //         if (bAlreadyShownInvalidFileTypeMessage === false) {
    //           bAlreadyShownInvalidFileTypeMessage = true;
    //           const info = new MessageBoxParameters(
    //             `The file type ${ext.replace(';', '')} cannot be uploaded.`,
    //             'Invalid file selected',
    //             MessageBoxType.Ok
    //           );
    //           this.dialog.open(MessageBoxComponent, { data: info });
    //         }
    //         continue;
    //       }

    //       if (this.data.type.toLowerCase() === 'tasktype') {
    //         // check File Type is valid
    //         if (!files[key].name.toLowerCase().endsWith('.docx')) {
    //           if (bAlreadyShownInvalidFileTypeMessage === false) {
    //             bAlreadyShownInvalidFileTypeMessage = true;
    //             const info = new MessageBoxParameters(
    //               `You can only upload files with a file type of .docx for task types.`,
    //               'Invalid file selected',
    //               MessageBoxType.Ok
    //             );
    //             this.dialog.open(MessageBoxComponent, { data: info });
    //           }
    //           continue;
    //         }
    //       }

    //       const matchedFile = this.existingFiles.find(
    //         (x) => x.fileName.toLowerCase() === files[key].name.toLowerCase()
    //       );
    //       if (matchedFile) {
    //         const info = new MessageBoxParameters(
    //           `File ${files[key].name} already exists.  Replace?`,
    //           'Duplicate File',
    //           MessageBoxType.YesNo
    //         );
    //         this.dialog
    //           .open(MessageBoxComponent, { data: info })
    //           .afterClosed()
    //           .subscribe((result) => {
    //             if (result === 'yes') {
    //               this.addFile(files[key]);
    //             }
    //             // make sure the value is reset so if we re-select the same file again it will still fire the change event...
    //             // this.file.nativeElement.value = '';
    //           });
    //       } else {
    //         this.addFile(files[key]);
    //         // make sure the value is reset so if we re-select the same file again it will still fire the change event...
    //         // this.file.nativeElement.value = '';
    //       }
    //     }
    //   }
    // }
  }

  summaryTextChanged(value, name): void {
    this.fileSummaryList[name] = value;
  }

  addFile(file: File) {
    // only add the file if not already added...

    const info = new MessageBoxParameters(
      'File Name cannot contain #',
      'Cannot Upload File',
      MessageBoxType.Ok
    );

    if (!file.name.includes('#')) {
      const exists = Array.from(this.files).find(
        (x) => x.name.toLowerCase() === file.name.toLowerCase()
      );
      if (!exists) {
        this.files.add(file);
        this.fileCount = this.fileCount + 1;
      }
    } else {
      this.dialog.open(MessageBoxComponent, { data: info });
    }
  }

  removeFile(file: File) {
    this.files.delete(file);
    this.fileCount = this.fileCount - 1;
    this.filesDnD = this.filesDnD.filter(
      (thisFile) => thisFile.name != file.name
    );
  }

  addFiles() {
    // make sure the value is reset so if we re-select the same file again it will still fire the change event...
    this.file.nativeElement.value = '';

    if (this.fileCount < this.defaultDataService.maxFileUploadLimit + 1) {
      this.file.nativeElement.click();
    }
  }

  btnUploadOrClose() {
    // if everything was uploaded already, just close the dialog
    if (this.uploadSuccessful) {
      return this.dialogRef.close();
    }

    // set the component state to "uploading"
    this.uploading = true;
    this.canDeleteFiles = false;
    this.canAddFiles = false;

    // start the upload and save the progress map
    this.progress = this.fileService.upload(this.files, this.fileSummaryList);

    // convert the progress map into an array
    let errors = [];
    const allProgressObservables = [];
    for (const key in this.progress) {
      if (key) {
        this.progress[key].progress.subscribe((x: number) => {});
        allProgressObservables.push(this.progress[key].progress);
        allProgressObservables.push(
          this.progress[key].progressErrors.pipe(
            map((res) => {
              if (res) {
                errors.push(res);
              }
              return res;
            })
          )
        );
      }
    }

    // Adjust the state variables

    // The dialog should not be closed while uploading
    this.canBeClosed = false;
    this.dialogRef.disableClose = true;

    // Hide the cancel-button
    this.showCancelButton = false;

    errors = [];

    // When all progress-observables are completed...
    forkJoin(allProgressObservables)
      .pipe(
        catchError((err) => {
          console.log('Upload error', err);

          // no point in keeping this dialog open so close the dialog
          this.dialogRef.close();

          // we have had an error so display a messagebox to user indicating there was a problem
          const info = new MessageBoxParameters(
            'There was a problem uploading the selected file(s).  Please check and try again.',
            'Error uploading selected file(s)',
            MessageBoxType.Ok
          );
          this.dialog.open(MessageBoxComponent, {
            data: info,
            disableClose: false,
            autoFocus: false,
          });

          return of(err);
        })
      )
      .subscribe((end) => {
        // ... the dialog can be closed again...
        this.canBeClosed = true;
        this.dialogRef.disableClose = false;

        // ... the upload was successful... Convert this button into a 'Finish' button
        this.uploadSuccessful = true;
        this.primaryButtonText = 'FINISH';

        // ... and the component is no longer uploading
        this.uploading = false;
        this.canAddFiles = false;

        // Update list of files.
        this.fileService.getFiles();

        // we have finished - close the dialog
        this.dialogRef.close();

        if (errors.length > 0) {
          // we have had an error so display a messagebox to user indicating there was a problem
          const info = new MessageBoxParameters(
            'There was a problem uploading the selected file(s).  Please check and try again.',
            'Error uploading selected file(s)',
            MessageBoxType.Ok
          );
          this.dialog.open(MessageBoxComponent, {
            data: info,
            disableClose: false,
            autoFocus: false,
          });
        }
      });
  }
}
